import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { FileText, ArrowUpRight, ArrowDownRight, Scale, ChevronDown, ChevronRight, Banknote, Trash2, Plus, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FeatureTour, useTourReplay } from '@/components/FeatureTour';
import { useBranchStore, useAuthStore } from '@/stores';
import { PurchaseClassificationBanner } from '@/components/accounting/PurchaseClassificationBanner';
import { localToday } from '@/lib/dateRange';

export default function VATReturnReportPage() {
  const [data, setData] = useState(null);
  const f = useReportFilters({ withUnassigned: true });
  const dateFilter = f.range;
  const [country, setCountry] = useState('sa');
  const [countries, setCountries] = useState([]);
  const { branches, fetchBranches } = useBranchStore();
  const [loading, setLoading] = useState(true);
  const [showSalesDetail, setShowSalesDetail] = useState(false);
  const [showPurchasesDetail, setShowPurchasesDetail] = useState(false);
  const [vatPayments, setVatPayments] = useState([]);
  const [totalVatPaid, setTotalVatPaid] = useState(0);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'bank', reference: '', date: localToday(), notes: '', paying_branch_id: '', bank_account_id: '' });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payReview, setPayReview] = useState(null);
  const [previewV2, setPreviewV2] = useState(false);
  const isAdmin = ['admin', 'owner', 'super_admin'].includes(useAuthStore((st) => st.user?.role));

  useEffect(() => {
    api.get('/accounting/vat-return/countries').then(r => setCountries(r.data)).catch(() => {});
    api.get('/bank-accounts').then(r => setBankAccounts(Array.isArray(r.data) ? r.data : r.data?.accounts || [])).catch(() => {});
    fetchBranches();
    fetchVatPayments();
  }, []);

  const fetchVatPayments = async () => {
    try {
      const res = await api.get('/accounting/vat-payments');
      setVatPayments(res.data.payments || []);
      setTotalVatPaid(res.data.total_paid || 0);
    } catch {}
  };

  const submitVatPayment = async (confirm = false) => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error('Enter amount'); return; }
    try {
      await api.post('/accounting/vat-payments', { ...paymentForm, amount: parseFloat(paymentForm.amount), confirm });
      toast.success('VAT payment recorded');
      setShowPaymentForm(false);
      setPayReview(null);
      setPaymentForm({ amount: '', payment_method: 'bank', reference: '', date: localToday(), notes: '', paying_branch_id: '', bank_account_id: '' });
      fetchVatPayments();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d) { setPayReview(d); return; }
      toast.error(typeof d === 'string' ? d : 'Failed to record payment');
    }
  };

  const deleteVatPayment = async (id) => {
    if (!window.confirm('Delete this VAT payment?')) return;
    try { await api.delete(`/accounting/vat-payments/${id}`); toast.success('Deleted'); fetchVatPayments(); }
    catch { toast.error('Failed'); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      // Only pass `country` when user explicitly picked one; otherwise let
      // the backend resolve the country from branch/tenant config so each
      // branch gets its correct return format (ZATCA vs FTA vs NBR, etc.)
      if (country && country !== 'auto') params.country = country;
      if (f.branchIds.length) params.branch_ids = f.branchIds.join(',');
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (previewV2) params.preview_v2 = true;
      const res = await api.get('/accounting/vat-return', { params });
      setData(res.data);
    } catch { toast.error('Failed to load VAT Return'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params, country, previewV2]);

  const s = data?.summary || {};
  const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const { replay: replayTour, forceStart: forceTour, replayKey } = useTourReplay('vat-return-v1');
  const tourSteps = [
    { selector: '[data-testid="vat-return-header"] h1', title: 'VAT Return', content: 'A complete, ZATCA-compliant VAT return — Output VAT on sales, Input VAT on purchases, and what you owe (or get back).', placement: 'bottom' },
    { selector: '[data-testid="country-select-2"]', title: 'Switch Country Format', content: 'Choose between Saudi ZATCA (16 boxes), UAE FTA, or Generic formats. Each uses the correct VAT rate and box layout.', placement: 'bottom' },
    { selector: '[data-testid="output-vat-2"]', title: 'Output & Input VAT', content: 'Output VAT is what you collected from customers. Input VAT is what you paid to suppliers. The difference is what you owe.', placement: 'bottom' },
    { selector: '[data-tour="vat-payments"]', title: 'Record VAT Payments', content: 'When you pay VAT to the tax authority, record it here by Cash or Bank — it stays as a history and reduces the payable.', placement: 'top' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vat-return-report-page-2">
        <ReportHeader icon={Scale} title="VAT Return" subtitle={data?.country_name || 'Loading...'} testId="vat-return-header"
          actions={<>
            <button onClick={replayTour} data-testid="tour-replay-btn" title="Replay tour"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-info bg-info/10 border border-info/30 rounded-lg hover:bg-info/20">
              <HelpCircle size={14} /> Tour
            </button>
            {isAdmin && (
              <button onClick={() => setPreviewV2(v => !v)} data-testid="vat-preview-v2-toggle"
                title="Read-only preview of the corrected purchase calculation (not approved, nothing is saved)"
                className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border ${previewV2 ? 'bg-warning/20 border-warning/40 text-warning' : 'bg-muted border-border text-muted-foreground'}`}>
                {previewV2 ? 'Preview: corrected (not approved)' : 'Preview corrected calculation'}
              </button>
            )}
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="country-select-2"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                {countries.map(c => (<SelectItem key={c.code} value={c.code}>{c.name} ({c.rate}%)</SelectItem>))}
              </SelectContent>
            </Select>
          </>} />
        <ReportFilterBar testId="vat-return-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds} range={f.range} onRange={f.setRange}>
          <ExportTrio disabled={!data} testId="vat-return-export" spec={() => ({
            title: `VAT Return — ${data?.country_name || ''}`, subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'vat_return',
            cols: [{ key: 'item', label: 'Item' }, { key: 'value', label: 'Amount', numeric: true }],
            rows: Object.entries(data?.summary || {}).filter(([, v]) => typeof v === 'number').map(([k, v]) => ({ item: k.replace(/_/g, ' '), value: v })),
          })} />
        </ReportFilterBar>

        {loading ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground">Loading...</div>
        ) : data && (
          <>
            <PurchaseClassificationBanner data={data} fmt={fmt} />
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-success/30 bg-success/10">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpRight className="w-4 h-4 text-success" />
                    <p className="text-[10px] uppercase tracking-wider text-success font-semibold">Output VAT (Sales)</p>
                  </div>
                  <p className="text-2xl font-bold text-success" data-testid="output-vat-2">{fmt(s.output_vat)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.output_taxable_count} taxable of {s.output_total_count} sales</p>
                </CardContent>
              </Card>
              <Card className="border-error/30 bg-error/10">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownRight className="w-4 h-4 text-error" />
                    <p className="text-[10px] uppercase tracking-wider text-error font-semibold">Input VAT (Purchases)</p>
                  </div>
                  <p className="text-2xl font-bold text-error" data-testid="input-vat">{fmt(s.input_vat)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.input_taxable_count} taxable of {s.input_total_count} purchases</p>
                </CardContent>
              </Card>
              <Card className={`border-2 ${s.net_vat >= 0 ? 'border-warning/30 bg-warning/10' : 'border-info/30 bg-info/10'}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Scale className="w-4 h-4 text-muted-foreground" />
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Net VAT {s.status === 'payable' ? 'Payable' : s.status === 'refundable' ? 'Refundable' : 'Nil'}</p>
                  </div>
                  <p className={`text-2xl font-bold ${s.net_vat >= 0 ? 'text-warning' : 'text-info'}`} data-testid="net-vat">{fmt(Math.abs(s.net_vat))}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.status === 'payable' ? 'You owe this to the tax authority' : s.status === 'refundable' ? 'Tax authority owes you' : 'No VAT due'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Period & Breakdown Mini */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Net Sales</p><p className="text-lg font-bold text-foreground">{fmt(s.output_net_sales)}</p></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Gross Sales</p><p className="text-lg font-bold text-foreground">{fmt(s.output_total_sales)}</p></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Net Purchases</p><p className="text-lg font-bold text-foreground">{fmt(s.input_net_purchases)}</p></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Gross Purchases</p><p className="text-lg font-bold text-foreground">{fmt(s.input_total_purchases)}</p></CardContent></Card>
            </div>

            {/* Country-Specific Box Format */}
            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b bg-muted/50">
                  <h2 className="text-sm font-semibold text-foreground">{data.country_name} - VAT Return Boxes</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Period: {data.period?.start} to {data.period?.end}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="vat-boxes-table">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground w-12">Box</th>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Description</th>
                        <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Amount</th>
                        <th className="text-right p-3 text-xs font-semibold text-muted-foreground">VAT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.boxes?.map((box) => {
                        const isTotal = box.type.includes('total') || box.type === 'net';
                        return (
                          <tr key={box.box} className={`border-b ${isTotal ? 'bg-muted/50 font-semibold' : ''} ${box.type === 'net' ? 'bg-warning/10' : ''}`}>
                            <td className="p-3 text-muted-foreground">{box.box}</td>
                            <td className="p-3 text-foreground">{box.label}</td>
                            <td className="p-3 text-right text-foreground">{fmt(box.amount)}</td>
                            <td className={`p-3 text-right font-medium ${box.type === 'net' ? (box.vat >= 0 ? 'text-warning' : 'text-info') : box.type.includes('output') ? 'text-success' : box.type.includes('input') ? 'text-error' : 'text-muted-foreground'}`}>
                              {fmt(box.type === 'net' ? Math.abs(box.vat) : box.vat)}
                              {box.type === 'net' && <span className="text-xs ml-1">({box.vat >= 0 ? 'Payable' : 'Refundable'})</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Sales Details (Drilldown) */}
            <Card>
              <CardContent className="p-0">
                <button onClick={() => setShowSalesDetail(!showSalesDetail)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors" data-testid="toggle-sales-detail">
                  <div className="flex items-center gap-2">
                    {showSalesDetail ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <h3 className="text-sm font-semibold text-foreground">Output VAT Details (Sales)</h3>
                    <span className="text-xs bg-success/20 text-success px-2 py-0.5 rounded-full">{data.details?.sales?.length || 0}</span>
                  </div>
                  <p className="text-sm font-bold text-success">{fmt(s.output_vat)}</p>
                </button>
                {showSalesDetail && (
                  <div className="border-t">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-muted/50 border-b">
                          <th className="p-2 text-left text-muted-foreground">Date</th>
                          <th className="p-2 text-left text-muted-foreground">Ref</th>
                          <th className="p-2 text-left text-muted-foreground">Customer</th>
                          <th className="p-2 text-right text-muted-foreground">Net</th>
                          <th className="p-2 text-right text-muted-foreground">Rate</th>
                          <th className="p-2 text-right text-muted-foreground">VAT</th>
                          <th className="p-2 text-right text-muted-foreground">Total</th>
                        </tr></thead>
                        <tbody>
                          {data.details?.sales?.map((r, i) => (
                            <tr key={i} className="border-b hover:bg-muted/50">
                              <td className="p-2 text-muted-foreground">{r.date?.split('T')[0]}</td>
                              <td className="p-2 text-muted-foreground font-mono">{r.ref}</td>
                              <td className="p-2 text-foreground">{r.description}</td>
                              <td className="p-2 text-right">{fmt(r.net)}</td>
                              <td className="p-2 text-right text-muted-foreground">{r.vat_rate}%</td>
                              <td className="p-2 text-right text-success font-medium">{fmt(r.vat)}</td>
                              <td className="p-2 text-right font-medium">{fmt(r.total)}</td>
                            </tr>
                          ))}
                          {(!data.details?.sales?.length) && (
                            <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No taxable sales in this period</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Purchases Details (Drilldown) */}
            <Card>
              <CardContent className="p-0">
                <button onClick={() => setShowPurchasesDetail(!showPurchasesDetail)}
                  className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors" data-testid="toggle-purchases-detail">
                  <div className="flex items-center gap-2">
                    {showPurchasesDetail ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <h3 className="text-sm font-semibold text-foreground">Input VAT Details (Purchases)</h3>
                    <span className="text-xs bg-error/20 text-error px-2 py-0.5 rounded-full">{data.details?.purchases?.length || 0}</span>
                  </div>
                  <p className="text-sm font-bold text-error">{fmt(s.input_vat)}</p>
                </button>
                {showPurchasesDetail && (
                  <div className="border-t">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-muted/50 border-b">
                          <th className="p-2 text-left text-muted-foreground">Date</th>
                          <th className="p-2 text-left text-muted-foreground">Ref</th>
                          <th className="p-2 text-left text-muted-foreground">Description</th>
                          <th className="p-2 text-right text-muted-foreground">Net</th>
                          <th className="p-2 text-right text-muted-foreground">Rate</th>
                          <th className="p-2 text-right text-muted-foreground">VAT</th>
                          <th className="p-2 text-right text-muted-foreground">Total</th>
                        </tr></thead>
                        <tbody>
                          {data.details?.purchases?.map((r, i) => (
                            <tr key={i} className="border-b hover:bg-muted/50">
                              <td className="p-2 text-muted-foreground">{r.date?.split('T')[0]}</td>
                              <td className="p-2 text-muted-foreground font-mono">{r.ref}</td>
                              <td className="p-2 text-foreground">{r.description}</td>
                              <td className="p-2 text-right">{fmt(r.net)}</td>
                              <td className="p-2 text-right text-muted-foreground">{r.vat_rate}%</td>
                              <td className="p-2 text-right text-error font-medium">{fmt(r.vat)}</td>
                              <td className="p-2 text-right font-medium">{fmt(r.total)}</td>
                            </tr>
                          ))}
                          {(!data.details?.purchases?.length) && (
                            <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No taxable purchases in this period</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* VAT Payment Section */}
            <Card data-tour="vat-payments">
              <CardContent className="p-0">
                <div className="p-4 border-b bg-muted/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Banknote size={16} className="text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">VAT Payments to Authority</h3>
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Total Paid: {cur()} {totalVatPaid.toLocaleString(undefined, {minimumFractionDigits:2})}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setShowPaymentForm(!showPaymentForm)} className="h-8 text-xs rounded-full" data-testid="add-vat-payment-btn">
                    <Plus size={14} className="mr-1" />{showPaymentForm ? 'Cancel' : 'Record Payment'}
                  </Button>
                </div>
                {showPaymentForm && (
                  <div className="p-4 border-b bg-info/10 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Amount *</Label><Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})} placeholder="0.00" className="h-9" /></div>
                      <div><Label className="text-xs">Method</Label>
                        <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm({...paymentForm, payment_method: v})}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="online">Online</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Date</Label><Input type="date" value={paymentForm.date} onChange={e => setPaymentForm({...paymentForm, date: e.target.value})} className="h-9" /></div>
                      <div><Label className="text-xs">Reference</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})} placeholder="Ref #" className="h-9" data-testid="vat-pay-reference" /></div>
                      <div><Label className="text-xs">Paying branch</Label>
                        <Select value={paymentForm.paying_branch_id || 'none'} onValueChange={v => setPaymentForm({...paymentForm, paying_branch_id: v === 'none' ? '' : v})}>
                          <SelectTrigger className="h-9" data-testid="vat-pay-branch"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">Company (head office)</SelectItem>{(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {paymentForm.payment_method === 'bank' && (
                        <div><Label className="text-xs">Bank account *</Label>
                          <Select value={paymentForm.bank_account_id || 'none'} onValueChange={v => setPaymentForm({...paymentForm, bank_account_id: v === 'none' ? '' : v})}>
                            <SelectTrigger className="h-9" data-testid="vat-pay-bank"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">Select…</SelectItem>{bankAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name || a.bank_name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    {payReview && (
                      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs space-y-1" data-testid="vat-pay-review">
                        <div className="font-semibold text-amber-800">{payReview.message}</div>
                        {(payReview.existing_vat_payments || []).map(p => <div key={p.id}>Existing VAT payment {p.date} · {cur()} {p.amount} · {p.reference || '-'}</div>)}
                        {(payReview.matching_expenses || []).map(x => <div key={x.id}>Expense {x.id.slice(0, 8)} {x.date?.slice(0, 10)} · {x.category} · {cur()} {x.amount}</div>)}
                        {(payReview.suggested_bank_lines || []).map(l => <div key={`${l.statement_id}-${l.line}`} className="text-muted-foreground">Bank line (suggestion) {l.date} · {cur()} {l.amount} · {l.description}</div>)}
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPayReview(null)} data-testid="vat-pay-review-cancel">Cancel</Button>
                          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={() => submitVatPayment(true)} data-testid="vat-pay-review-confirm">Not a duplicate — save</Button>
                        </div>
                      </div>
                    )}
                    <Button size="sm" onClick={() => submitVatPayment(false)} className="rounded-full bg-teal-600 hover:bg-teal-700" data-testid="submit-vat-payment">Save Payment</Button>
                  </div>
                )}
                {vatPayments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted/50 border-b">
                        <th className="p-2.5 text-left text-muted-foreground">Date</th>
                        <th className="p-2.5 text-right text-muted-foreground">Amount</th>
                        <th className="p-2.5 text-left text-muted-foreground">Method</th>
                        <th className="p-2.5 text-left text-muted-foreground">Reference</th>
                        <th className="p-2.5 text-left text-muted-foreground">Paid by</th>
                        <th className="p-2.5 w-10"></th>
                      </tr></thead>
                      <tbody>
                        {vatPayments.map(p => (
                          <tr key={p.id} className="border-b hover:bg-muted/50">
                            <td className="p-2.5 text-muted-foreground">{p.date}</td>
                            <td className="p-2.5 text-right font-medium text-foreground">{cur()} {(p.amount || 0).toLocaleString(undefined, {minimumFractionDigits:2})}</td>
                            <td className="p-2.5"><span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.payment_method === 'bank' ? 'bg-info/20 text-info' : p.payment_method === 'cash' ? 'bg-success/20 text-success' : 'bg-purple-100 text-purple-700'}`}>{p.payment_method === 'bank' ? 'Bank' : p.payment_method === 'cash' ? 'Cash' : 'Online'}</span></td>
                            <td className="p-2.5 text-muted-foreground font-mono">{p.reference || '-'}</td>
                            <td className="p-2.5 text-muted-foreground">{p.paying_branch_name || 'Company'}{p.bank_account_name ? ` · ${p.bank_account_name}` : ''}</td>
                            <td className="p-2.5"><button onClick={() => deleteVatPayment(p.id)} className="p-1 rounded hover:bg-error/10 text-red-400 hover:text-error"><Trash2 size={12} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 text-center text-muted-foreground text-sm">No VAT payments recorded yet</div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <FeatureTour key={replayKey} tourId="vat-return-v1" steps={tourSteps} forceStart={forceTour} />
    </DashboardLayout>
  );
}
